import { useMemo } from 'react'
import { Link } from '@tanstack/react-router'
import { Play } from 'lucide-react'

export type HelpVideo = {
  id: string
  title: string
  description?: string
  durationSec?: number
  src: string
  thumb?: string
  transcript?: string
  createdAt?: string
}

export type HelpManifest = {
  version: number
  videos: HelpVideo[]
}

export interface HelpScreenProps {
  manifest: HelpManifest | null
  isLoading?: boolean
  error?: string | null
  activeVideoId?: string | null
  onSelectVideo?: (id: string) => void
}

function formatDuration(durationSec?: number) {
  if (!durationSec || durationSec <= 0) return null
  const minutes = Math.floor(durationSec / 60)
  const seconds = Math.floor(durationSec % 60)
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

export function HelpScreen(props: HelpScreenProps) {
  const {
    manifest,
    isLoading,
    error,
    activeVideoId,
    onSelectVideo,
  } = props
  const videos = manifest?.videos ?? []
  const hasVideos = videos.length > 0

  const activeVideo = useMemo(
    () => videos.find((video) => video.id === activeVideoId) ?? null,
    [videos, activeVideoId],
  )

  return (
    <div className="bg-slate-50 text-slate-900">
      <main className="mx-auto w-full max-w-container px-4 py-8">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold">Help</h1>
          <p className="text-sm text-slate-600">
            Quick walkthroughs for common tasks.
          </p>
        </header>

        <section className="mt-6 space-y-6">
          <div className="space-y-3">
            <h2 className="text-base font-semibold text-slate-900">
              Articles
            </h2>
            <div className="grid gap-4 md:grid-cols-2">
              <Link
                to="/help/callout-status-changes"
                className="block rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:bg-slate-50"
              >
                <h3 className="text-base font-semibold text-slate-900">
                  Callout status changes
                </h3>
                <p className="mt-1 text-sm text-slate-600">
                  What each status means, what you can change it to, and what
                  gets recorded.
                </p>
              </Link>
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="text-base font-semibold text-slate-900">
              Videos
            </h2>
            {isLoading ? (
              <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">
                Loading help videos...
              </div>
            ) : error ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">
                {error}
              </div>
            ) : !hasVideos ? (
              <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">
                No help videos found.
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {videos.map((video) => {
                  const isActive = video.id === activeVideoId
                  const duration = formatDuration(video.durationSec)
                  return (
                    <article
                      key={video.id}
                      className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
                    >
                      <button
                        type="button"
                        className="group relative block w-full overflow-hidden bg-slate-900/80"
                        onClick={() => onSelectVideo?.(video.id)}
                        aria-label={`Play ${video.title}`}
                      >
                        {isActive && activeVideo ? (
                          <video
                            className="aspect-video w-full object-cover"
                            src={activeVideo.src}
                            poster={activeVideo.thumb}
                            controls
                            autoPlay
                          >
                            {activeVideo.transcript ? (
                              <track
                                kind="subtitles"
                                src={activeVideo.transcript}
                                srcLang="en"
                                label="English"
                                default
                              />
                            ) : null}
                          </video>
                        ) : (
                          <>
                            {video.thumb ? (
                              <img
                                src={video.thumb}
                                alt=""
                                className="aspect-video w-full object-cover opacity-95 transition group-hover:opacity-100"
                              />
                            ) : (
                              <div className="flex aspect-video items-center justify-center bg-slate-800 text-sm text-slate-200">
                                No thumbnail
                              </div>
                            )}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 text-slate-900 shadow">
                                <Play className="h-5 w-5 translate-x-[1px]" />
                              </div>
                            </div>
                          </>
                        )}
                      </button>

                      <div className="space-y-2 px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <h3 className="text-base font-semibold">
                            {video.title}
                          </h3>
                          {duration ? (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                              {duration}
                            </span>
                          ) : null}
                        </div>
                        {video.description ? (
                          <p className="text-sm text-slate-600">
                            {video.description}
                          </p>
                        ) : null}
                      </div>
                    </article>
                  )
                })}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  )
}
